import React, { useEffect, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/Button";
import ComboBoxDynamic from "../../../components/ComboBoxDynamic/ComboBoxDynamic";
import ComboBoxStation from "../../../components/ComboBoxStation";
import DataTable from "../../../components/DataTable";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";
import EmptyState from "../../../components/EmptyState/EmptyState";
import { Input, Select } from "../../../components/Inputs";
import Loading from "../../../components/Loading";
import ModalCertificate from "../../../components/Modal/ModalCertificate";
import ModalConfirmation from "../../../components/Modal/ModalConfirmation";
import { resourceTypes } from "../../../constants/selects";
import { monthsAndResourceTypes } from "../../../constants/translations";
import { API_POST } from "../../../helpers/api";
import { convertAmountToFrontend } from "../../../helpers/convert";
import { setNotification } from "../../../helpers/notification";
import {
  getCertificatesRequest,
  setSelectedToOrganization,
} from "../../../redux/actions/bilateralAgreements";
import {
  setCertificateFilterExportStartDate,
  setCertificateFilterProductionPeriod,
  setCertificateFilterResourceType,
  setCertificateFilterStation,
} from "../../../redux/actions/certificates";

const BilateralAgreements = ({ t }) => {
  const dispatch = useDispatch();
  const { activeList, activeLoading, total } = useSelector(
    (state) => state.bilateralAgreements.certificates
  );
  const organizationId = useSelector(
    (state) => state.authorization.user.organization.organizationId
  );
  const selectedToOrganization = useSelector(
    (state) => state.bilateralAgreements.selectedToOrganization
  );

  const filterStation = useSelector(
    (state) => state.certificates.filterStation
  );
  const filterResourceType = useSelector(
    (state) => state.certificates.filterResourceType
  );
  const filterProductionPeriodMonth = useSelector(
    (state) => state.certificates.filterProductionPeriodMonth
  );
  const filterProductionPeriodYear = useSelector(
    (state) => state.certificates.filterProductionPeriodYear
  );

  const filterExportStartDateMonth = useSelector(
    (state) => state.certificates.filterExportStartDateMonth
  );
  const filterExportStartDateYear = useSelector(
    (state) => state.certificates.filterExportStartDateYear
  );

  const documentCount = useSelector(
    (state) => state.bilateralAgreements.certificates.documentCount
  );
  const [sortedColumn, setSortedColumn] = useState("productionPeriod");
  const [sortDirection, setSortDirection] = useState("desc");
  const [activePage, setActivePage] = useState(0);
  const [bilateralAgreementsCertificates, setBilateralAgreementsCertificates] =
    useState([]);
  const [inspectedCertificateId, setInspectedCertificateId] = useState();
  const [totalAmount, setTotalAmount] = useState(0);
  const [
    sendBilateralAgreementConfirmationModal,
    setSendBilateralAgreementConfirmationModal,
  ] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);

  const translations = monthsAndResourceTypes(t);

  const selectResourceType = resourceTypes(t);

  const activeCertificatesColumns = [
    {
      name: t("pages.default.certificates.table.stationName"),
      selector: "stationName",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.certificates.table.resourceType"),
      selector: "resourceType",
      sortable: true,
      cell: (row) => {
        return translations[row.resourceType];
      },
    },
    {
      name: t("pages.default.certificates.table.productionPeriod"),
      selector: "month",
      sortable: true,
      cell: (row) => {
        return `${translations[row.month]}/${row.year}`;
      },
    },
    {
      name: t("pages.default.certificates.table.exportPeriod"),
      selector: "exportDate",
      sortable: true,
      cell: (row) =>
        moment(row.exportDate).isValid()
          ? moment(row.exportDate).format("DD/MM/YYYY HH:mm:ss")
          : "-",
    },
    {
      name: t("pages.default.certificates.table.documentCount"),
      selector: "amount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.amount);
      },
    },
    {
      name: t("pages.default.certificates.table.process"),
      selector: "process",
      sortable: false,
      width: "140px",
      cell: (row, index) => {
        return (
          <div style={{ display: "flex" }}>
            <Button
              title={t("ui.default.review")}
              suffixIcon="search"
              type="secondary"
              onClick={() => inspectCertificate(row)}
              id={`ba-review-${index}`}
            />
            <Button
              title={t("ui.default.add")}
              suffixIcon="plus"
              type="primary"
              onClick={() => addCertificateToBilateralAgreement(row)}
              disabled={row.amount === 0 || addCertificateButtonIsDisabled(row)}
              id={`ba-add-${index}`}
            />
          </div>
        );
      },
    },
  ];

  const bilateralAgreementsCertificatesColumns = [
    {
      name: t("pages.default.certificates.table.stationName"),
      selector: "stationName",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.certificates.table.resourceType"),
      selector: "resourceType",
      sortable: true,
      cell: (row) => {
        return translations[row.resourceType];
      },
    },
    {
      name: t("pages.default.certificates.table.productionPeriod"),
      selector: "month",
      sortable: true,
      cell: (row) => {
        return `${translations[row.month]}/${row.year}`;
      },
    },
    {
      name: t("pages.default.certificates.table.documentCount"),
      selector: "amount",
      sortable: false,
      cell: (row, index) => {
        return (
          <Input
            type="number"
            min={1}
            max={row.amount}
            pattern="[0-9]*"
            value={row.bilateralAmount ? row.bilateralAmount : ""}
            placeholder={`Max ${row.amount}`}
            onChange={(e) => bilateralAmountChange(row, e)}
            id={`ba-input-${index}`}
          />
        );
      },
    },
    {
      name: t("pages.default.certificates.table.process"),
      selector: "process",
      sortable: false,
      width: "140px",
      cell: (row, index) => {
        return (
          <div style={{ display: "flex" }}>
            <Button
              title={t("ui.default.remove")}
              suffixIcon="minus"
              type="destructive"
              onClick={() => removeCertificateFromBilateralAgreements(row)}
              id={`ba-remove-${index}`}
            />
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    console.log(JSON.stringify(documentCount));
  }, [documentCount]);

  useEffect(() => {
    dispatch(
      getCertificatesRequest({
        certificateStatus: ["ACTIVE"],
      })
    );
  }, [dispatch]);

  useEffect(() => {
    const totalAmount = bilateralAgreementsCertificates.reduce(
      (acc, currentVal) => {
        return (acc += currentVal.bilateralAmount);
      },
      0
    );
    setTotalAmount(totalAmount);
  }, [bilateralAgreementsCertificates]);

  const filterChange = (e) => {
    let splitted = [];
    let payload = {};
    splitted = e.target.value.split("-");
    payload = {
      month: splitted[1] || "",
      year: splitted[0] || "",
    };
    switch (e.target.name) {
      case "exportStartDate":
        dispatch(setCertificateFilterExportStartDate(payload));
        break;

      case "productionPeriod":
        dispatch(setCertificateFilterProductionPeriod(payload));
        break;
      case "resourceType":
        dispatch(setCertificateFilterResourceType(e.target.value));
        break;
      default:
        break;
    }
  };

  const selectStation = (station) => {
    dispatch(setCertificateFilterStation(station));
  };

  const inspectCertificate = (row) => {
    setInspectedCertificateId(row.id);
  };

  const addCertificateToBilateralAgreement = (row) => {
    setBilateralAgreementsCertificates((prevState) => [
      ...prevState,
      { ...row, bilateralAmount: 0 },
    ]);
  };

  const addCertificateButtonIsDisabled = (row) =>
    bilateralAgreementsCertificates.find(
      (certificate) => certificate.id === row.id
    );

  const removeCertificateFromBilateralAgreements = (row) => {
    setBilateralAgreementsCertificates((prevState) => {
      const updatedBilateralAgreementsCertificates = [...prevState];
      const bilateralCertificateIndex =
        updatedBilateralAgreementsCertificates.findIndex(
          (certificate) => certificate.id === row.id
        );
      updatedBilateralAgreementsCertificates.splice(
        bilateralCertificateIndex,
        1
      );
      return updatedBilateralAgreementsCertificates;
    });
  };

  const bilateralAmountChange = (row, e) => {
    const { value } = e.target;
    setBilateralAgreementsCertificates((prevState) => {
      const updatedBilateralAgreementsCertificates = [...prevState];
      const foundedCertificate = updatedBilateralAgreementsCertificates.find(
        (certificate) => certificate.id === row.id
      );
      foundedCertificate.bilateralAmount = value === "" ? 0 : parseInt(value);
      return updatedBilateralAgreementsCertificates;
    });
  };

  const createBilateralAgreementDisabled = () => {
    const amountInputCheck =
      bilateralAgreementsCertificates.filter(
        (certificate) => certificate.bilateralAmount === 0
      ).length > 0;
    return amountInputCheck || Object.keys(selectedToOrganization).length === 0;
  };

  const sendBilateralAgreementRequest = async () => {
    try {
      setLoadingRequest(true);
      const bilateralAgreementDetails = bilateralAgreementsCertificates.map(
        (certificate) => ({
          amount: certificate.bilateralAmount,
          yekgCertificateId: certificate.id,
        })
      );
      await API_POST("bilateralAgreement", {
        status: "INITIAL",
        amount: totalAmount,
        bilateralAgreementDetails,
        fromOrganization: organizationId,
        toOrganization: selectedToOrganization.organizationId,
      });
      setNotification(
        "success",
        t("pages.default.bilateralAgreements.success")
      );
      setBilateralAgreementsCertificates([]);
      filterCertificates();
      dispatch(setSelectedToOrganization({}));
      setLoadingRequest(false);
      setSendBilateralAgreementConfirmationModal(false);
    } catch (err) {
      setLoadingRequest(false);
      setNotification("error", err.response.data.message);
    }
  };

  const filterCertificates = () => {
    dispatch(
      getCertificatesRequest({
        stationId: filterStation ? filterStation.stationId : "",
        resourceType: filterResourceType === "-" ? null : filterResourceType,
        month: filterProductionPeriodMonth,
        year: filterProductionPeriodYear,
        exportStartDateMonth: filterExportStartDateMonth,

        exportStartDateYear: filterExportStartDateYear,

        certificateStatus: ["ACTIVE"],
      })
    );
  };

  const handleClick = (activePage, selector, direction) => {
    console.log("page", activePage, selector, direction);
    setActivePage(activePage);
    dispatch(
      getCertificatesRequest({
        stationId: filterStation ? filterStation.stationId : "",
        resourceType: filterResourceType === "-" ? null : filterResourceType,
        month: filterProductionPeriodMonth,
        year: filterProductionPeriodYear,
        exportStartDateMonth: filterExportStartDateMonth,
        exportStartDateYear: filterExportStartDateYear,
        certificateStatus: ["ACTIVE"],
        page: activePage,
        selector: selector,
        direction: direction,
      })
    );
  };
  return (
    <>
      <h5 className="km__u--text-has-emphasis">
        {t("pages.default.bilateralAgreements.title")}
      </h5>
      <div className="km__p--card">
        <div className="km__p--bilateral-agreements-filter-container">
          <div className="km__u--text_title">
            {t("pages.default.bilateralAgreements.activeCertificates")}
          </div>
          <div className="km__p--bilateral-agreements-filters">
            <ComboBoxStation
              t={t}
              selectedStation={filterStation}
              selectStation={selectStation}
              id="ba-station"
            />
            <Input
              type="month"
              placeholder={t(
                "pages.default.certificates.filters.exportStartDate"
              )}
              name="exportStartDate"
              placement="bottom-end"
              value={
                filterExportStartDateYear
                  ? `${filterExportStartDateYear}-${filterExportStartDateMonth}`
                  : ""
              }
              onChange={filterChange}
            />
            <Select
              options={selectResourceType}
              name="resourceType"
              defaultValue={filterResourceType}
              onChange={filterChange}
              id="ba-resourceType"
            />
            <Input
              type="month"
              placeholder={t(
                "pages.default.certificates.filters.allProductionPeriod"
              )}
              name="productionPeriod"
              value={
                filterProductionPeriodYear
                  ? `${filterProductionPeriodYear}-${filterProductionPeriodMonth}`
                  : ""
              }
              onChange={filterChange}
              id="ba-productionPeriod"
            />
            <Button
              label={t("ui.default.filter")}
              onClick={filterCertificates}
              id="ba-filter"
            />
          </div>
        </div>
        {!activeLoading && (
          <DataTableServerSideRendering
            columns={activeCertificatesColumns}
            data={activeList}
            rowKey="id"
            sortedColumn={sortedColumn}
            setSortedColumn={setSortedColumn}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            pagination
            activePage={activePage}
            totalPageCount={total?.totalPageCount}
            handleFetchData={handleClick}
            subTotals={[
              {
                totalCount: documentCount ? documentCount.totalCount : 0,
                countInPage: documentCount ? documentCount.countInPage : 0,
                sumIndex: 4,
              },
            ]}
          />
        )}
        {activeList.length === 0 && !activeLoading && (
          <EmptyState
            icon="file-text"
            text={t("pages.default.certificates.activeNotFound")}
          />
        )}
        {activeLoading && (
          <Loading text={t("pages.default.certificates.activeLoading")} />
        )}
      </div>
      {inspectedCertificateId && (
        <ModalCertificate
          t={t}
          certificateId={inspectedCertificateId}
          isOpen={inspectedCertificateId ? true : false}
          toggle={() => setInspectedCertificateId()}
          difCalculate={true}
        />
      )}

      {bilateralAgreementsCertificates.length > 0 && (
        <>
          <div className="km__p--card">
            <div className="km__p--bilateral-agreements-header">
              <div className="km__u--text_title">
                {t("pages.default.bilateralAgreements.bilateralAgreementTitle")}
              </div>
            </div>

            <DataTable
              columns={bilateralAgreementsCertificatesColumns}
              data={bilateralAgreementsCertificates}
              rowKey={"id"}
              pagination
              paginationPerPage={10}
            />

            <div className="km__p--bilateral-agreements-execute-container">
              <div className="km__u--text_title">
                {t("pages.default.bilateralAgreements.totalAmountTitle")}
              </div>
              <div className="km__p--bilateral-agreements-total-amount">
                {totalAmount}
              </div>
              <div className="km__p--bilateral-agreements-execute">
                <div>
                  {t(
                    "pages.default.bilateralAgreements.chooseSystemParticipant"
                  )}
                  :
                </div>
                <ComboBoxDynamic
                  inputPlaceholder={t(
                    "pages.default.bilateralAgreements.input.chooseSystemParticipantPlaceholder"
                  )}
                  selectedOrganization={selectedToOrganization}
                  organizationSelect={setSelectedToOrganization}
                  filterOption={{
                    name: "organizationId",
                    value: organizationId,
                  }}
                  id="ba-systemParticipant"
                />
                <Button
                  label={t(
                    "pages.default.bilateralAgreements.createButtonTitle"
                  )}
                  onClick={() =>
                    setSendBilateralAgreementConfirmationModal(true)
                  }
                  disabled={createBilateralAgreementDisabled()}
                  id="ba-create"
                />
              </div>
            </div>
          </div>
          <ModalConfirmation
            isOpen={sendBilateralAgreementConfirmationModal}
            toggle={() =>
              setSendBilateralAgreementConfirmationModal(
                !sendBilateralAgreementConfirmationModal
              )
            }
            header={t("pages.default.bilateralAgreements.confirmation.header")}
            body={t("pages.default.bilateralAgreements.confirmation.body")}
            approve={sendBilateralAgreementRequest}
            loading={loadingRequest}
          />
        </>
      )}
    </>
  );
};

export default BilateralAgreements;
